/** @jsx jsx */
import * as React from 'react';
import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import {
  Menu,
  MenuItem as RKMenuItem,
  MenuItemProps as RKMenuItemProps,
  MenuSeparator,
  MenuInitialState,
  MenuItemCheckbox,
  MenuItemCheckboxProps,
  useMenuState,
} from 'reakit/Menu';
import {
  PolymorphicComponentProps,
  PolymorphicComponent,
} from 'react-polymorphic-box';
import { CKCheckbox, CKCheckboxProps } from '../CKCheckbox';

export interface CKActionMenuItemOwnProps extends Partial<RKMenuItemProps> {
  children: React.ReactNode;
  isDestructive?: boolean;
  icon?: React.ReactNode;
  accessory?: React.ReactNode;
  isDisabled?: boolean;
}

export interface CKCheckboxMenuItemProps
  extends Pick<CKCheckboxProps, 'checked' | 'id' | 'onChange'> {
  className?: string;
  isDestructive?: boolean;
  isDisabled?: boolean;
}

export type CKActionMenuItemProps<
  E extends React.ElementType
> = PolymorphicComponentProps<E, CKActionMenuItemOwnProps>;

const defaultElement = 'button';

export interface CKActionMenuComposition {
  Item: PolymorphicComponent<CKActionMenuItemOwnProps, typeof defaultElement>;
  Divider: React.FC;
  Checkbox: React.FC<CKCheckboxMenuItemProps>;
}

export interface CKActionMenuProps {
  className?: string;
  options?: MenuInitialState;
}

export const CKActionMenu: React.FC<CKActionMenuProps> &
  CKActionMenuComposition = ({ children, options, className, ...rest }) => {
  const menuState = useMenuState(options);
  const menuClass = cx('ck-menu', className);

  return (
    <Menu
      className={menuClass}
      css={css`
        outline: 0;
        &:focus {
          outline: 0;
        }
      `}
      {...menuState}
      {...rest}
      visible
    >
      {React.Children.map(children, child => {
        // @ts-ignore
        return React.cloneElement(child, menuState);
      })}
    </Menu>
  );
};

const MenuItemInner: React.FC<Pick<
  CKActionMenuItemOwnProps,
  'icon' | 'accessory'
>> = ({ children, icon, accessory }) => {
  return (
    <React.Fragment>
      {accessory && (
        <span className="ck-action-item-accessory">{accessory}</span>
      )}
      {icon && <span className="ck-action-item-icon">{icon}</span>}
      <span className="ck-action-item-label">{children}</span>
    </React.Fragment>
  );
};

const CKActionMenuItem = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      children,
      icon,
      isDestructive,
      isDisabled,
      className,
      ...rest
    }: CKActionMenuItemProps<E>,
    innerRef: typeof rest.ref
  ) => {
    const itemClass = cx('ck-action-item', className, {
      'ck-action-item--destroy': isDestructive,
    });

    return (
      // @ts-ignore
      <RKMenuItem
        ref={innerRef}
        disabled={isDisabled}
        className={itemClass}
        {...rest}
      >
        <MenuItemInner children={children} icon={icon} />
      </RKMenuItem>
    );
  }
) as <E extends React.ElementType = typeof defaultElement>(
  props: CKActionMenuItemProps<E>
) => JSX.Element;

const MenuDivider: React.FC = props => (
  <MenuSeparator className="ck-menu-divider" {...props} />
);

const CKActionMenuItemCheckbox: React.FC<CKCheckboxMenuItemProps> = props => {
  const {
    children,
    id,
    checked,
    onChange,
    className,
    isDestructive,
    isDisabled,
    ...rest
  } = props;

  const itemClass = cx('ck-action-item', className, {
    'ck-action-item--destroy': isDestructive,
  });

  return (
    <MenuItemCheckbox
      as="button"
      {...(rest as MenuItemCheckboxProps)}
      className={itemClass}
      checked={checked}
      onChange={onChange}
      type="button"
      disabled={isDisabled}
    >
      <MenuItemInner
        children={children}
        accessory={
          <CKCheckbox
            className="pointer-events-none"
            id={id}
            checked={checked}
            label={null}
          />
        }
      />
    </MenuItemCheckbox>
  );
};

CKActionMenu.Item = CKActionMenuItem;
CKActionMenu.Checkbox = CKActionMenuItemCheckbox;
CKActionMenu.Divider = MenuDivider;
